import { useClipboard } from '@mantine/hooks';
import {
  Box, Button, Flex, IconButton, Text
} from '@huspy/briks-web';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import {
  Check,
  Copy,
  Download,
  Mail,
  Whatsapp
} from '@huspy/briks-icons';
import { Group } from '@mantine/core';
import briksTheme from '@shared/briks-theme';
import { ProposalCreationConfiguratorRoute } from '@modules/proposals/presentation/routes';
import useGetProposalById from '@modules/proposals/api/query/useGetProposalById';
import { USER_EVENTS } from '@shared/analytics/events';
import Loader from '@shared/loader';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import useRequestDigitalProposalPdf from '@modules/proposals/api/mutation/useRequestDigitalProposalPdf';
import { shareToEmail, shareToWhatsapp } from '@shared/utils';
import { getClientVaultDigitalProposalUrl } from '@modules/core/utils';
import { useTranslation } from 'react-i18next';
import { Grid } from '@huspy/styled-system/jsx';
import { ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { useCallback, useEffect } from 'react';
import { initProposalDownload } from '@modules/proposals/presentation/utils';
import { ProposalTemplate } from '@huspy/forge/templates';
import { mapProposalEntityToTemplateData } from '../../mappers/mapProposalEntityToTemplateData';

type Props = {
  opened: boolean;
  close: () => void;
  open: () => void;
  proposal?: ProposalEntity;
  onDone?: () => void;
  doneText?: string;
};
export const ProposalShareSlido = ({
  close, open: _open, opened, proposal, onDone, doneText,
}: Props) => {
  let routeProposalId;

  try {
  // Attempt to extract the id from the route parameters if available
    const params = ProposalCreationConfiguratorRoute.useParams();
    routeProposalId = params.id;
  } catch {
  // Ignore any errors if useParams isn't available
    routeProposalId = undefined;
  }

  const id = proposal?.external_id ?? routeProposalId!; // Use proposal ID if available, otherwise use routeProposalId

  const { t } = useTranslation();
  const { data } = useGetProposalById(id, mapProposalEntityToTemplateData);

  const clipboard = useClipboard({ timeout: 5000 });

  const { mutateAsync: downloadProposalPDF, isPending: isDownloadProposalPDFPending } = useRequestDigitalProposalPdf();
  const fileName = `${t('proposals.fileNamePrefix')} - ${data?.proposal.clientName} ${data?.proposal.clientLastName}.pdf`;

  const handleDownloadProposal = useCallback(async () => {
    await initProposalDownload(
      fileName,
      'proposal-template',
      downloadProposalPDF
    );
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.DOWNLOADED);
  }, [downloadProposalPDF, fileName]);

  useEffect(() => {
    if (id && !!data?.proposal.mortgageDetails.downpayment) {
      trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_VIEWED_STARTED, {
        useContext: true,
        addToContext: true,
        proposal_id: id,
      });
    }
  }, [id, data?.proposal.mortgageDetails.downpayment]);

  return (
    <BaseSlido
      opened={ opened }
      close={ close }
      title={ (
        <Flex direction='column' gap='2' p='6' pb='0'>
          <Text size='5xl'>
            Share this proposal
          </Text>
        </Flex>
      ) }
      footer={ (
        <Flex justify='end' gap='2'>
          <Button
            data-test='save-btn'
            onClick={ () => (onDone ? onDone() : close()) }
          >
            { doneText ?? <>Done</> }
          </Button>
        </Flex>
      ) }
    >
      <Flex h='100%' direction='column' pt='10' px='6' gap='8'>
        <Text size='2xl' weight='medium'>
          Select where or how you want this proposal to be shared
        </Text>
        <Grid gap='4' columns={ 2 }>
          <Flex
            direction='column'
            bg='bg-secondary'
            py='6'
            px='4'
            borderColor='border-secondary'
            border='1px solid'
            borderRadius='4'
            justifyContent='center'
            alignItems='center'
            cursor='pointer'
            onClick={ () => {
              shareToWhatsapp(
                // eslint-disable-next-line max-len
                `Here's a proposal with the bank rates, fees and upfront costs created according to your specific needs for you to evaluate and compare: ${getClientVaultDigitalProposalUrl(id)}`
              );
              trackAmplitudeEvent(USER_EVENTS.PROPOSAL.SHARE_VIA_WHATSAPP);
            } }
          >
            <IconButton
              variant='secondary'
              size='xl'
              style={ { padding: '12px' } }

            >
              <Whatsapp
                style={ { color: '#10B981' } }
              />
            </IconButton>
            <Text size='md' weight='medium'>WhatsApp</Text>
          </Flex>
          <Flex
            direction='column'
            bg='bg-secondary'
            py='6'
            px='4'
            borderColor='border-secondary'
            border='1px solid'
            cursor='pointer'
            borderRadius='4'
            justifyContent='center'
            alignItems='center'
            onClick={ () => {
              shareToEmail(
                'Digital Proposal Preview',
                // eslint-disable-next-line max-len
                `Here's a proposal with the bank rates, fees and upfront costs created according to your specific needs for you to evaluate and compare: ${getClientVaultDigitalProposalUrl(id)}`
              );
              trackAmplitudeEvent(USER_EVENTS.PROPOSAL.SHARE_VIA_EMAIL);
            } }
          >
            <IconButton
              variant='secondary'
              size='xl'
              style={ { padding: '12px' } }

            >
              <Mail />
            </IconButton>
            <Text size='md' weight='medium'>Email</Text>
          </Flex>
          <Flex
            direction='column'
            bg='bg-secondary'
            py='6'
            px='4'
            borderColor='border-secondary'
            border='1px solid'
            borderRadius='4'
            cursor='pointer'
            justifyContent='center'
            alignItems='center'
            onClick={ () => {
              clipboard.copy(getClientVaultDigitalProposalUrl(id));
              trackAmplitudeEvent(USER_EVENTS.PROPOSAL.LINK_COPIED);
            } }
          >
            <IconButton
              variant='secondary'
              size='xl'
              style={ { padding: '12px' } }
            >
              {clipboard.copied ? (
                <Check style={ { color: briksTheme.colors['success.600'] } } />
              ) : <Copy />}
            </IconButton>
            <Text size='md' weight='medium'>Copy link</Text>
          </Flex>
          <Flex
            direction='column'
            bg='bg-secondary'
            py='6'
            px='4'
            borderColor='border-secondary'
            border='1px solid'
            cursor='pointer'
            borderRadius='4'
            justifyContent='center'
            alignItems='center'
            onClick={ handleDownloadProposal }
          >
            <IconButton
              variant='secondary'
              size='xl'
              style={ { padding: '12px' } }
              disabled={ isDownloadProposalPDFPending }
            >
              { isDownloadProposalPDFPending ? <Loader /> : <Download /> }
            </IconButton>
            <Text size='md' weight='medium'>Download</Text>
          </Flex>
        </Grid>
        {clipboard.copied && (
          <Box borderRadius='2' py='3' px='4' bg='bg-quaternary'>
            <Group>
              <IconButton variant='secondary' borderRadius='8px'>
                <Check />
              </IconButton>
              <Text size='md' color='text-primary'>Link copied to your clipboard</Text>
            </Group>
          </Box>
        )}
      </Flex>
      <Box display='none'>
        <ProposalTemplate
          { ...data! }
        />
      </Box>
    </BaseSlido>
  );
};
